import React, { useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { Box, Button } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  ChooseFileField,
  LinkField,
  HtmlField,
  NameField,
  DescriptionField,
  TagsField,
  CommentsField,
  newContentFormFields
} from "./NewContentFields";
import { filesPreloading } from "../../services/fileUploadService";
import ProcessFileModal from "../ProcessFileModal/ProcessFileModal";
import { processVideoMimeTypes } from "../../constants";

const useStyles = makeStyles(theme => ({
  textFieldsContainer: {
    "& > div": {
      marginBottom: theme.spacing(4),
      "& > p": {
        marginBottom: theme.spacing(-1.5)
      }
    }
  }
}));

const getNewVersionSchema = t => {
  return Yup.object().shape({
    [newContentFormFields.name]: Yup.string()
      .required(t("validationMessages.nameRequired"))
      .max(
        255,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.name"),
          maxLength: 255
        })
      ),
    [newContentFormFields.description]: Yup.string().max(
      350,
      t("validationMessages.cannotExceedLength", {
        type: t("addNewContent.addItem.description"),
        maxLength: 350
      })
    ),
    [newContentFormFields.comment]: Yup.string().max(
      350,
      t("validationMessages.cannotExceedLength", {
        type: t("addNewContent.addItem.comments"),
        maxLength: 350
      })
    ),
    [newContentFormFields.tags]: Yup.array().of(
      Yup.string().max(
        255,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.tags"),
          maxLength: 255
        })
      )
    ),
    [newContentFormFields.file]: Yup.mixed().test(
      "fileRequired",
      t("validationMessages.fileRequired"),
      function() {
        return (
          !!this.parent[newContentFormFields.file] ||
          !!this.parent[newContentFormFields.link] ||
          !!this.parent[newContentFormFields.content]
        );
      }
    ),
    [newContentFormFields.link]: Yup.mixed().test(
      "linkRequired",
      t("validationMessages.linkRequired"),
      function() {
        return (
          !!this.parent[newContentFormFields.file] ||
          !!this.parent[newContentFormFields.link] ||
          !!this.parent[newContentFormFields.content]
        );
      }
    )
  });
};

const AddNewContentVersionForm = ({ defaultData, onSubmit, isSubmitting }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [filesList, setFilesList] = useState([]);
  const [processVideoModal, setProcessVideoModal] = useState(false);

  const saveForm = async values => {
    if (
      defaultData.itemType === "file" &&
      defaultData.extendedItemType === "page"
    ) {
      const tmpObj = await filesPreloading(filesList, values.content);
      values.images = tmpObj.images;
      values.media = tmpObj.media;
      values.attachments = tmpObj.attachments;
      values.content = tmpObj.content;
    }

    onSubmit(values);
  };

  return (
    <Formik
      initialValues={{
        [newContentFormFields.file]: null,
        [newContentFormFields.link]: defaultData.link,
        [newContentFormFields.content]: defaultData.content,
        [newContentFormFields.comment]: "",
        [newContentFormFields.name]: defaultData.name,
        [newContentFormFields.description]: defaultData.description || "",
        [newContentFormFields.tags]: defaultData.tags
      }}
      validationSchema={getNewVersionSchema(t)}
      onSubmit={values => {
        values &&
        values.file &&
        processVideoMimeTypes.includes(values.file.type)
          ? setProcessVideoModal(true)
          : saveForm(values);
      }}
    >
      {({ submitForm, setFieldValue, values, setSubmitting }) => (
        <Form className={classes.textFieldsContainer}>
          {(["file", "video"].includes(defaultData.itemType) ||
            (defaultData.itemType === "extended" &&
              defaultData.extendedItemType === "scorm")) &&
            defaultData.extendedItemType !== "page" && (
              <ChooseFileField disabled={isSubmitting} />
            )}
          {defaultData.itemType === "link" && (
            <LinkField disabled={isSubmitting} />
          )}
          <NameField
            label={t("addNewContent.addItem.titleField")}
            disabled={isSubmitting}
          />
          {defaultData.itemType === "file" &&
            defaultData.extendedItemType === "page" && (
              <HtmlField
                isSubmitting={isSubmitting}
                defaultData={defaultData}
                setFieldValue={setFieldValue}
                values={values}
                setFilesList={setFilesList}
                filesList={filesList}
              />
            )}
          <DescriptionField disabled={isSubmitting} />
          <TagsField disabled={isSubmitting} />
          <CommentsField disabled={isSubmitting} />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              onClick={() => {
                submitForm();
              }}
            >
              {t("addNewContent.addItem.submit")}
            </Button>
          </Box>
          <ProcessFileModal
            uploadVideo={() => {
              setProcessVideoModal(false);
              saveForm(values);
            }}
            onClose={() => {
              setProcessVideoModal(false);
              setSubmitting(false);
            }}
            open={processVideoModal}
          />
        </Form>
      )}
    </Formik>
  );
};
export default AddNewContentVersionForm;

AddNewContentVersionForm.propTypes = {
  /**
   * Called when the form is submitted
   */
  onSubmit: PropTypes.func,
  /**
   * Submitting state for formik
   */
  isSubmitting: PropTypes.bool,
  /**
   * The default data that will be used to populate the form
   */
  defaultData: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string,
    link: PropTypes.string,
    file: PropTypes.object,
    associatedCourses: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string
      })
    ),
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string
      })
    )
  })
};
